import { useState, useRef, useCallback, useEffect } from 'react';
import {
  BOMB_SOUNDS,
  BOMB_DROP_SOUNDS,
  BOMB_KICK_SOUNDS,
  BOMB_STOP_SOUNDS,
  BOMB_BOUNCE_SOUNDS,
  UPGRADE_PICKUP_SOUNDS,
  SUDDEN_DEATH_START_SOUND,
  SUDDEN_DEATH_BLOCK_SOUNDS,
  ALL_SOUNDS,
  OST_TRACKS
} from '../shared/constants';

const useGameAudio = () => {
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [volume, setVolume] = useState(() => {
    const saved = localStorage.getItem('bomberman-volume');
    return saved ? parseFloat(saved) : 0.4; // Default to 40%
  });
  
  const audioEnabledRef = useRef(false);
  const volumeRef = useRef(0.4);
  
  // Soundtrack state
  const [currentSoundtrack, setCurrentSoundtrack] = useState(null);
  const soundtrackAudioRef = useRef(null);
  const soundtrackStartTimeRef = useRef(null);

  // Keep refs in sync with state
  useEffect(() => {
    audioEnabledRef.current = audioEnabled;
  }, [audioEnabled]);

  useEffect(() => {
    volumeRef.current = volume;
    localStorage.setItem('bomberman-volume', volume.toString());
    
    // Update soundtrack volume when slider changes
    if (soundtrackAudioRef.current) {
      soundtrackAudioRef.current.volume = volume * 1;
    }
  }, [volume]);

  // Function to enable audio (requires user interaction)
  const enableAudio = useCallback(async () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      await audioContext.resume();
      setAudioEnabled(true);
    } catch (error) {
      console.warn('Audio context resume failed:', error);
    }
  }, []);

  // Function to handle volume change
  const handleVolumeChange = useCallback(async (event) => {
    const newVolume = parseFloat(event.target.value);
    setVolume(newVolume);
    
    // Always try to enable audio when volume slider is changed
    // This helps reconnected players re-enable sounds
    if (!audioEnabled && newVolume > 0) {
      await enableAudio();
    }
  }, [audioEnabled, enableAudio]);

  // Function to play random bomb explosion sound
  const playRandomBombSound = useCallback(async () => {
    if (!audioEnabledRef.current || volumeRef.current === 0) {
      return;
    }
    try {
      const randomSound = BOMB_SOUNDS[Math.floor(Math.random() * BOMB_SOUNDS.length)];
      const audioPath = `${process.env.PUBLIC_URL}/sounds/${randomSound}`;
      
      // Fetch the audio file as blob to bypass MIME type issues
      const response = await fetch(audioPath);
      if (!response.ok) {
        return;
      }
      
      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      audio.volume = volumeRef.current * 0.5; // Apply global volume with sound-specific multiplier
      
      audio.addEventListener('ended', () => {
        URL.revokeObjectURL(audioUrl); // Clean up blob URL
      });
      
      await audio.play();
    } catch (error) {
      console.warn('Failed to play bomb sound:', error);
    }
  }, []);

  // Function to play random bomb drop sound
  const playRandomBombDropSound = useCallback(() => {
    if (!audioEnabledRef.current || volumeRef.current === 0) return;
    const randomSound = BOMB_DROP_SOUNDS[Math.floor(Math.random() * BOMB_DROP_SOUNDS.length)];
    const audio = new Audio(`${process.env.PUBLIC_URL}/sounds/${randomSound}`);
    audio.volume = volumeRef.current * 0.4; // Apply global volume with sound-specific multiplier
    audio.play().catch(() => {});
  }, []);

  // Function to play random bomb kick sound
  const playRandomBombKickSound = useCallback(() => {
    if (!audioEnabledRef.current || volumeRef.current === 0) return;
    const randomSound = BOMB_KICK_SOUNDS[Math.floor(Math.random() * BOMB_KICK_SOUNDS.length)];
    const audio = new Audio(`${process.env.PUBLIC_URL}/sounds/${randomSound}`);
    audio.volume = volumeRef.current * 0.4; // Apply global volume with sound-specific multiplier
    audio.play().catch(() => {});
  }, []);

  // Function to play random bomb stop sound
  const playRandomBombStopSound = useCallback(() => {
    if (!audioEnabledRef.current || volumeRef.current === 0) return;
    const randomSound = BOMB_STOP_SOUNDS[Math.floor(Math.random() * BOMB_STOP_SOUNDS.length)];
    const audio = new Audio(`${process.env.PUBLIC_URL}/sounds/${randomSound}`);
    audio.volume = volumeRef.current * 0.3; // Apply global volume with sound-specific multiplier
    audio.play().catch(() => {});
  }, []);

  // Function to play random bomb bounce sound
  const playRandomBombBounceSound = useCallback(() => {
    if (!audioEnabledRef.current || volumeRef.current === 0) return;
    const randomSound = BOMB_BOUNCE_SOUNDS[Math.floor(Math.random() * BOMB_BOUNCE_SOUNDS.length)];
    const audio = new Audio(`${process.env.PUBLIC_URL}/sounds/${randomSound}`);
    audio.volume = volumeRef.current * 0.4; // Apply global volume with sound-specific multiplier
    audio.play().catch(() => {});
  }, []);

  // Function to play random upgrade pickup sound
  const playRandomUpgradePickupSound = useCallback(() => {
    if (!audioEnabledRef.current || volumeRef.current === 0) return;
    
    // Check if UPGRADE_PICKUP_SOUNDS exists and has content
    if (!UPGRADE_PICKUP_SOUNDS || UPGRADE_PICKUP_SOUNDS.length === 0) {
      console.warn('UPGRADE_PICKUP_SOUNDS is not defined or empty');
      return;
    }
    
    const randomSound = UPGRADE_PICKUP_SOUNDS[Math.floor(Math.random() * UPGRADE_PICKUP_SOUNDS.length)];
    const audio = new Audio(`${process.env.PUBLIC_URL}/sounds/${randomSound}`);
    audio.volume = volumeRef.current * 0.5; // Apply global volume with sound-specific multiplier
    audio.play().catch(() => {});
  }, []);

  // Function to play sudden death start sound
  const playSuddenDeathStartSound = useCallback(() => {
    if (!audioEnabledRef.current || volumeRef.current === 0) return;
    
    const audio = new Audio(`${process.env.PUBLIC_URL}/sounds/${SUDDEN_DEATH_START_SOUND}`);
    audio.volume = volumeRef.current * 1; // Louder for dramatic effect
    audio.play().catch(() => {});
  }, []);

  // Function to play random sudden death block sound
  const playRandomSuddenDeathBlockSound = useCallback(() => {
    if (!audioEnabledRef.current || volumeRef.current === 0) return;
    
    if (!SUDDEN_DEATH_BLOCK_SOUNDS || SUDDEN_DEATH_BLOCK_SOUNDS.length === 0) {
      console.warn('SUDDEN_DEATH_BLOCK_SOUNDS is not defined or empty');
      return;
    }
    
    const randomSound = SUDDEN_DEATH_BLOCK_SOUNDS[Math.floor(Math.random() * SUDDEN_DEATH_BLOCK_SOUNDS.length)];
    const audio = new Audio(`${process.env.PUBLIC_URL}/sounds/${randomSound}`);
    audio.volume = volumeRef.current * 0.5; // Quieter since many blocks spawn
    audio.play().catch(() => {});
  }, []);

  // Add a ref to track current soundtrack to avoid state closure issues
  const currentSoundtrackRef = useRef(null);
  
  // Function to handle soundtrack playback
  const handleSoundtrackUpdate = useCallback((soundtrack, soundtrackStartTime) => {
    if (!audioEnabledRef.current || volumeRef.current === 0) return;
    
    // If no soundtrack or same soundtrack already playing, do nothing
    if (!soundtrack || currentSoundtrackRef.current === soundtrack) return;
    
    try {
      // Stop current soundtrack if playing - use the robust stop function
      stopSoundtrack();
      
      // Start new soundtrack
      const audioPath = `${process.env.PUBLIC_URL}/sounds/ost/${soundtrack}`;
      const audio = new Audio(audioPath);
      audio.volume = volumeRef.current * 0.3; // Lower volume for background music
      audio.loop = true;
      
      // Calculate current playback position based on server time
      const currentTime = Date.now();
      const elapsedTime = (currentTime - soundtrackStartTime) / 1000; // Convert to seconds
      
      // Set playback position when audio is loaded
      audio.addEventListener('loadeddata', () => {
        if (audio.duration && elapsedTime > 0) {
          audio.currentTime = elapsedTime % audio.duration;
        }
        audio.play().catch(() => {});
      });
      
      soundtrackAudioRef.current = audio;
      setCurrentSoundtrack(soundtrack);
      currentSoundtrackRef.current = soundtrack;
      soundtrackStartTimeRef.current = soundtrackStartTime;
      
    } catch (error) {
      console.warn('Failed to play soundtrack:', error);
    }
  }, []);

  // Function to stop soundtrack
  const stopSoundtrack = useCallback(() => {
    try {
      if (soundtrackAudioRef.current) {
        soundtrackAudioRef.current.pause();
        soundtrackAudioRef.current.currentTime = 0; // Reset to beginning
        soundtrackAudioRef.current.src = ''; // Clear the source
        soundtrackAudioRef.current.load(); // Force reload to release resources
        soundtrackAudioRef.current = null;
      }
    } catch (error) {
      console.warn('Error stopping soundtrack:', error);
      soundtrackAudioRef.current = null; // Ensure we clear the ref even if there's an error
    }
    
    setCurrentSoundtrack(null);
    currentSoundtrackRef.current = null;
    soundtrackStartTimeRef.current = null;
  }, []);

  // Preload all audio files using the reliable method
  useEffect(() => {
    const preloadAudio = async () => {
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();

        const preloadPromises = ALL_SOUNDS.map(async (soundFile) => {
          try {
            const response = await fetch(`${process.env.PUBLIC_URL}/sounds/${soundFile}`);
            const arrayBuffer = await response.arrayBuffer();
            const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
            return { name: soundFile, buffer: audioBuffer };
          } catch (err) {
            console.warn(`Failed to preload sound: ${soundFile}`, err);
            return null;
          }
        });

        const loadedSounds = await Promise.all(preloadPromises);
        return loadedSounds;
      } catch (error) {
        console.warn('Audio preloading failed:', error);
      }
    };

    // IMPORTANT: only run after user interaction to bypass autoplay restrictions
    const startPreload = () => {
      preloadAudio();
      document.removeEventListener("click", startPreload);
      document.removeEventListener("keydown", startPreload);
    };

    // Listen for any user interaction to start preloading
    document.addEventListener("click", startPreload);
    document.addEventListener("keydown", startPreload);

    return () => {
      document.removeEventListener("click", startPreload);
      document.removeEventListener("keydown", startPreload);
    };
  }, []);

  // Function to play specific poop sound for diarrhea disease
  const playPoopSound = useCallback((soundFile) => {
    if (!audioEnabledRef.current || volumeRef.current === 0) return;
    
    const audio = new Audio(`${process.env.PUBLIC_URL}/sounds/${soundFile}`);
    audio.volume = volumeRef.current * 0.6; // Apply global volume with sound-specific multiplier
    audio.play().catch(() => {});
  }, []);

  return {
    // State
    audioEnabled,
    volume,
    audioEnabledRef,
    volumeRef,
    currentSoundtrack,
    
    // Functions
    enableAudio,
    handleVolumeChange,
    playRandomBombSound,
    playRandomBombDropSound,
    playRandomBombKickSound,
    playRandomBombStopSound,
    playRandomBombBounceSound,
    playRandomUpgradePickupSound,
    playSuddenDeathStartSound,
    playRandomSuddenDeathBlockSound,
    handleSoundtrackUpdate,
    stopSoundtrack,
    playPoopSound
  };
};

export default useGameAudio;